/**
 * Showing whichever tab is in front, using the reader that already exists.
 *
 * # Why tabs multiplex one reader rather than owning one each
 *
 * A tab holds an address, and the app already has exactly one piece of state
 * meaning "the message being read" — `store.openEmailId` and its neighbours.
 * Giving every tab its own copy would mean a second way to be reading
 * something, and every feature that touches the open message (marking read,
 * flagging, the reprieve rows, the reading pane's own scroll) would have to
 * learn about both.
 *
 * So switching tabs sets the open message, and the pinned tab remembers what it
 * had. The cost is a fetch when a tab comes forward, which is a round trip
 * against a local replica on the desktop build — the only build with tabs.
 *
 * The alternative worth naming, in case this becomes slow: cache the `Envelope`
 * per tab and hand it to `store.open` as the seed, which is exactly what
 * clicking a row already does. That is an optimisation and not a redesign, and
 * it can be added without changing anything above.
 */

import { effect } from "@preact/signals";

import * as store from "./store";
import * as tabs from "./tabs";

/**
 * What the pinned tab was reading before somebody switched away from it.
 *
 * Restored when it comes back, because the mail tab is a place people leave and
 * return to — losing the message they were part-way through would make the
 * strip cost something to use.
 */
let mainHeld: tabs.Address | null = null;

/** True while this module is the one writing to the store, so it ignores itself. */
let applying = false;

/**
 * Keep the open message and the front tab in step. Called once, from startup.
 *
 * Returns its own teardown so a test can stop it; the app never does.
 */
export function watch(): () => void {
  const stop = effect(() => {
    const tab = tabs.current.value;
    applying = true;
    try {
      if (tabs.isPinned(tab)) {
        // Back to the mail. Restore what it was reading, or clear the pane if
        // it was reading nothing -- leaving the last tab's message behind would
        // show it under a list that is not filtered to it.
        if (mainHeld) void store.openById(mainHeld.account, mainHeld.email);
        else store.closeEmail();
      } else if (tab.address) {
        void store.openById(tab.address.account, tab.address.email);
      }
    } finally {
      applying = false;
    }
  });

  // The other direction, and only for the pinned tab: opening a message from
  // the list while the mail is in front is what `mainHeld` is remembering.
  const remember = effect(() => {
    const open = store.openEmailId.value;
    if (applying) return;
    if (!tabs.isPinned(tabs.current.peek())) return;
    if (open === null) {
      mainHeld = null;
      return;
    }
    const cut = open.indexOf(":");
    if (cut < 1) return;
    mainHeld = { account: open.slice(0, cut), email: open.slice(cut + 1) };
  });

  /*
   * A message tab holds on to its message.
   *
   * Several things clear the open message and none of them knows about tabs:
   * archiving or deleting from the reading bar closes it, and so does a filter
   * that drops the row. In the shell that empties the reading pane beside a
   * list, which is fine and always has been. In a message tab it would leave
   * the tab in front with nothing in it.
   *
   * **Re-asserted rather than closed, deliberately.** Closing the tab was the
   * first answer and Amit rejected it: a tab is closed with the mouse, on the
   * close button, and nothing else should take one away. Archiving a message
   * does not stop it being a message -- it is still there and still readable --
   * so the tab goes on showing what it was opened to show, and the person
   * decides when they are done with it.
   *
   * Guarded by `applying` so that switching tabs, which passes through a moment
   * of the store still holding the old message, does not read as the message
   * having gone.
   */
  const hold = effect(() => {
    const open = store.openEmailId.value;
    if (applying || open !== null) return;
    const tab = tabs.current.peek();
    if (!tabs.isPinned(tab) && tab.address) {
      void store.openById(tab.address.account, tab.address.email);
    }
  });

  return () => {
    stop();
    remember();
    hold();
  };
}

/** Forget what the mail tab was reading. For signing out. */
export function reset(): void {
  mainHeld = null;
}
